import {
  Box,
  Container,
  Stack,
  Typography,
  Link,
  Divider,
  Grid,
  IconButton,
  useTheme,
} from '@mui/material';
import { Iconify, SocialIcon } from 'src/components/iconify';
import { Image } from 'src/components/image';
import { CONFIG } from 'src/config-global';
import { RouterLink } from 'src/routes/components';

export const Footer = () => {
  const theme = useTheme();
  const currentYear = new Date().getFullYear();

  const SPARK_SOCIALS = [
    {
      icon: 'simple-icons:x',
      url: CONFIG.site.socialMedia.twitter,
      label: 'Spark on X (Twitter)',
      key: 'twitter',
    },
    {
      icon: 'simple-icons:facebook',
      url: CONFIG.site.socialMedia.facebook,
      label: 'Spark on Facebook',
      key: 'facebook',
    },
    {
      icon: 'simple-icons:instagram',
      url: CONFIG.site.socialMedia.instagram,
      label: 'Spark on Instagram',
      key: 'instagram',
    },
    {
      icon: 'simple-icons:linkedin',
      url: CONFIG.site.socialMedia.linkedin,
      label: 'Spark on LinkedIn',
      key: 'linkedin',
    },
  ].filter((social) => social.url);

  return (
    <Box
      component="footer"
      sx={{
        pt: 6,
        pb: 0,
        bgcolor: 'background.neutral',
        borderTop: `1px solid ${theme.palette.divider}`,
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={4}>
          {/* Brand Section */}
          <Grid item xs={12} md={4}>
            <Stack spacing={2}>
              <Image
                height={32}
                src="/logo/logo-full.svg"
                alt="Spark Logo"
                sx={{
                  mr: 'auto',
                  '& img': {
                    objectFit: 'contain',
                    width: 'auto',
                    height: '100%',
                  },
                }}
              />
              <Typography
                variant="body2"
                color="text.secondary"
                className="font-spark-primary"
              >
                Discover. Ignite. Achieve.
              </Typography>
            </Stack>
          </Grid>

          {/* Quick Links */}
          <Grid item xs={12} md={4}>
            <Stack spacing={2}>
              <Typography
                variant="subtitle1"
                fontWeight="bold"
                className="font-spark-primary"
              >
                Quick Links
              </Typography>
              <Stack spacing={1.5}>
                <Link
                  component={RouterLink}
                  href={CONFIG.site.privacyPolicyUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  color="text.secondary"
                  underline="hover"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    fontSize: '0.8125rem',
                    transition: 'color 0.2s',
                    '&:hover': {
                      color: 'primary.main',
                    },
                  }}
                >
                  <Iconify icon="solar:shield-check-bold" width={16} />
                  Privacy Policy
                </Link>
                <Link
                  component={RouterLink}
                  href={CONFIG.site.termsAndConditionsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  color="text.secondary"
                  underline="hover"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    fontSize: '0.8125rem',
                    transition: 'color 0.2s',
                    '&:hover': {
                      color: 'primary.main',
                    },
                  }}
                >
                  <Iconify icon="solar:document-text-bold" width={16} />
                  Terms & Conditions
                </Link>
                <Link
                  component={RouterLink}
                  href={CONFIG.site.refundAndCancellationPolicyUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  color="text.secondary"
                  underline="hover"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    fontSize: '0.8125rem',
                    transition: 'color 0.2s',
                    '&:hover': {
                      color: 'primary.main',
                    },
                  }}
                >
                  <Iconify icon="heroicons:receipt-refund-solid" width={16} />
                  Refund & Cancellation
                </Link>
              </Stack>
            </Stack>
          </Grid>

          {/* Contact Information */}
          <Grid item xs={12} md={4}>
            <Stack spacing={2}>
              <Typography
                variant="subtitle1"
                fontWeight="bold"
                className="font-spark-primary"
              >
                Contact Us
              </Typography>
              <Stack spacing={1.5}>
                <Link
                  href={`tel:${CONFIG.site.phoneNumber}`}
                  color="text.secondary"
                  underline="hover"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    fontSize: '0.8125rem',
                    transition: 'color 0.2s',
                    '&:hover': {
                      color: 'primary.main',
                    },
                  }}
                >
                  <Iconify icon="solar:phone-bold" width={16} />
                  {CONFIG.site.phoneNumber}
                </Link>
                <Link
                  href={`mailto:${CONFIG.site.email}`}
                  color="text.secondary"
                  underline="hover"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    fontSize: '0.8125rem',
                    transition: 'color 0.2s',
                    '&:hover': {
                      color: 'primary.main',
                    },
                  }}
                >
                  <Iconify icon="solar:letter-bold" width={16} />
                  {CONFIG.site.email}
                </Link>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    color: 'text.secondary',
                  }}
                >
                  <Iconify icon="solar:map-point-bold" width={16} />
                  <Typography variant="body2" sx={{ fontSize: '0.8125rem' }}>
                    {CONFIG.site.address}
                  </Typography>
                </Box>
              </Stack>
            </Stack>
          </Grid>
        </Grid>

        <Divider sx={{ my: 4 }} />

        {/* Bottom Section - Social Media & App Store Links */}
        <Box
          sx={{
            pb: 3,
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: { xs: 'flex-start', md: 'center' },
            justifyContent: 'space-between',
            rowGap: 3,
            columnGap: 4,
          }}
        >
          {/* Social Media Section */}
          <Stack spacing={1.5} sx={{ width: { xs: '100%', md: 'auto' } }}>
            <Typography
              variant="caption"
              className="font-spark-primary"
              sx={{
                color: 'text.secondary',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: 1,
                fontSize: '0.75rem',
              }}
            >
              Follow Us
            </Typography>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              {SPARK_SOCIALS.map((social) => (
                <IconButton
                  key={social.key}
                  component={social.url ? 'a' : 'div'}
                  href={social.url || undefined}
                  target={social.url ? '_blank' : undefined}
                  rel={social.url ? 'noopener noreferrer' : undefined}
                  aria-label={social.label}
                  disabled={!social.url}
                  disableRipple
                  sx={{
                    color: 'text.secondary',
                    width: 40,
                    height: 40,
                  }}
                >
                  <SocialIcon width={20} icon={social.key}></SocialIcon>
                </IconButton>
              ))}
            </Stack>
          </Stack>

          {/* App Store Links */}
          <Stack
            spacing={1.5}
            sx={{ width: { xs: '100%', md: 'auto' } }}
            alignItems={{ xs: 'flex-start', md: 'flex-end' }}
          >
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: 1,
                fontSize: '0.75rem',
              }}
              className="font-spark-primary"
            >
              Download App
            </Typography>
            <Stack
              direction="row"
              alignItems="center"
              spacing={1.5}
              sx={{ flexWrap: 'wrap' }}
            >
              {CONFIG.site.appStore.googlePlay && (
                <Link
                  href={CONFIG.site.appStore.googlePlay}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Download on Google Play"
                  sx={{
                    display: 'inline-block',
                  }}
                >
                  <Image
                    src="/assets/images/footer/google-play.webp"
                    alt="Get it on Google Play"
                    sx={{
                      height: 50,
                      width: 'auto',
                      display: 'block',
                      '& img': {
                        objectFit: 'contain',
                        width: 'auto',
                        height: '100%',
                      },
                    }}
                  />
                </Link>
              )}
              {CONFIG.site.appStore.appleAppStore && (
                <Link
                  href={CONFIG.site.appStore.appleAppStore}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Download on Apple App Store"
                  sx={{
                    display: 'inline-block',
                  }}
                >
                  <Image
                    src="/assets/images/footer/apple-store.webp"
                    alt="Download on the App Store"
                    sx={{
                      height: 50,
                      width: 'auto',
                      display: 'block',
                      '& img': {
                        objectFit: 'contain',
                        width: 'auto',
                        height: '100%',
                      },
                    }}
                  />
                </Link>
              )}
            </Stack>
          </Stack>
        </Box>

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems="center"
          spacing={2}
          sx={{ pb: 3 }}
        >
          <Typography variant="body2" color="text.secondary">
            © {currentYear} {CONFIG.site.name}. All rights reserved.
          </Typography>
        </Stack>
      </Container>
    </Box>
  );
};
